import {
  Card, Divider, Grid, IconButton, InputAdornment, Link, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip,
} from '@material-ui/core';
import { ApiError } from 'api/api-error';
import animalApi from 'api/query/animal';
import catalogueApi, { CatalogueType } from 'api/query/catalogue';
import { CatalogueAnimal } from 'api/query/catalogue/catalogue-animal';
import { CatalogueAnimalError } from 'api/query/catalogue/catalogue-animal-error.enum';
import Button from 'components/button';
import { ConfirmationDialog } from 'components/confirmation-dialog/confirmation-dialog';
import FormErrorMessage from 'components/form-error-message';
import { AlertIcon } from 'components/icons/alert';
import { DeleteOutlinedIcon } from 'components/icons/delete-outlined';
import { DocCsvOutlinedIcon } from 'components/icons/doc-csv-outlined';
import { ExportOutlinedIcon } from 'components/icons/export-outlined';
import TableFade from 'components/table/table-fade';
import { ErrorMessage } from 'constants/error-message.enum';
import { getAnimalIdsFromCatalogue, getAnimalSex } from 'pages/catalogues/catalogue-utils';
import React, {
  ChangeEvent, useCallback, useEffect, useState,
} from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { defaultTextFieldProps } from 'theme/props/text-field';
import { getSexDesc } from 'utils/utils';
import { CatalogueFormField } from '../catalogue-form-field.enum';
import { catalogueFormValidation } from '../catalogue-form-validation';
import {
  checkDuplicateAnimalIds,
  getAnimalCount, getAnimals, getAnimalTemplateUrl, getCatalogueTypeBySaleType, isValidAnimalListingType,
} from '../create-catalogue-form-utils';
import { AddAnimalsProps } from './add-animals-props';
import './add-animals.scss';

/**
 * Add animals through file upload.
 *
 * Step 3 of the create/edit sales/semen catalogue.
 */
export const AddAnimals: React.FC<AddAnimalsProps> = (props) => {
  const {
    catalogue,
  } = props;

  const {
    register, unregister, errors, setValue, setError, clearError, control,
  } = useFormContext();
  const { fields, append, remove } = useFieldArray({ control, name: CatalogueFormField.Animals });
  const isSale = getCatalogueTypeBySaleType(catalogue.saleType) === CatalogueType.Sales;
  const isSemen = getCatalogueTypeBySaleType(catalogue.saleType) === CatalogueType.Semen;

  const [catalogueRetrieved, setCatalogueRetrieved] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [fileInputRef, setFileInputRef] = useState<HTMLInputElement>();
  const [catalogueAnimals, setCatalogueAnimals] = useState<CatalogueAnimal[]>([]);
  const [lineItemErrors, setLineItemErrors] = useState<ApiError[]>([]);
  // Store the file in local state to prevent it being deleted if the user
  // attempts to upload a new file then clicks cancel.
  const [animalFile, setAnimalFile] = useState<File>();
  const [confirmationOpen, setConfirmationOpen] = useState<boolean>(false);

  /**
   * Validates the animals.
   *
   * If successful, the animals will be returned.
   *
   * If there are any errors, no animals are returned.
   */
  const validateAnimals = useCallback(async (f: File) => {
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', f);
      formData.append('dbType', catalogue.dbType.toString());
      formData.append('type', getCatalogueTypeBySaleType(catalogue.saleType).toString());
      formData.append('catalogueId', `${catalogue.id}`);

      const response = await catalogueApi.validateAnimals(formData);
      const animals = response.animals || [];
      setCatalogueAnimals(animals);
      append(animals);

      // Find the first general error
      const generalErrorCodes = [CatalogueAnimalError.InvalidTemplate, CatalogueAnimalError.EmptyFile, CatalogueAnimalError.InvalidLotNumber];
      const generalError = response.errors.find((e) => generalErrorCodes.includes(e.errorType as number));

      // Filter the error messages for the line item errors
      const lineErrorCodes = [
        CatalogueAnimalError.InvalidAnimalFormat,
        CatalogueAnimalError.AnimalNotFound,
        CatalogueAnimalError.AnimalInExistingCatalogue,
      ];
      const lineErrors = response.errors.filter((e) => lineErrorCodes.includes(e.errorType as number));

      setLineItemErrors(lineErrors);

      if (generalError) {
        setError(CatalogueFormField.AnimalFile, 'data', generalError.errorMessage);
      }
    } catch (e) {
      setError(CatalogueFormField.AnimalFile, 'error', ErrorMessage.UnknownTryAgain);
    } finally {
      setLoading(false);
    }
  }, [append, catalogue.dbType, catalogue.id, catalogue.saleType, setError]);

  /**
   * Resets the animals and errors to their initial state.
   */
  const reset = useCallback(() => {
    setCatalogueAnimals([]);
    setLineItemErrors([]);
  }, []);

  /**
   * Handles the click event for the file input.
   *
   * If a valid file already exists, prevent the event and show the confirmation modal.
   * @param event
   */
  const inputClick = (event: any) => {
    // Display a confirmation modal if a valid file exists.
    if (catalogueAnimals.length
      && !errors[CatalogueFormField.AnimalFile]
      && !lineItemErrors.length) {
      event.preventDefault();
      setConfirmationOpen(true);
    }
  };

  /**
   * Closes the confirmation modal, unsets the file values and triggers
   * a click on the file input.
   */
  const confirmReplaceFile = () => {
    remove();
    reset();
    setConfirmationOpen(false);
    setAnimalFile(undefined);
    setValue(CatalogueFormField.AnimalFile, '');

    setTimeout(() => {
      // eslint-disable-next-line no-unused-expressions
      fileInputRef?.click();
    });
  };

  /**
   * Deletes an animal from the list.
   *
   * If this was the the last animal deleted, also reset the state and remove the file.
   * @param index
   * @param inputName
   */
  const deleteAnimal = (index: number) => {
    remove(index);
    const animals = [...catalogueAnimals];
    animals.splice(index, 1);
    setCatalogueAnimals(animals);

    if (animals.length === 0) {
      setAnimalFile(undefined);
      reset();
    }
  };

  /**
   * Checks if the file is valid (type and size).
   *
   * If valid, validates the animals.
   *
   * If invalid, displays error.
   */
  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files && event.currentTarget.files[0];

    if (file) {
      setAnimalFile(file);
      const isValidType = isValidAnimalListingType(file.type);

      if (!isValidType) {
        setError(CatalogueFormField.AnimalFile, 'type', 'File doesn\'t match required format, please upload the supplied template');
      }

      const isValidSize = file.size < 1e+6; // 1MB
      if (!isValidSize) {
        setError(CatalogueFormField.AnimalFile, 'size', 'File size must not exceed 1MB');
      }

      if (isValidType && isValidSize) {
        clearError(CatalogueFormField.Animals);

        // Check the file for duplicate animal ids
        const duplicates = await checkDuplicateAnimalIds(file);
        if (duplicates.length) {
          setLineItemErrors(duplicates);
        } else {
          validateAnimals(file);
        }
      }

      // Reset the value of the file input so it can be replaced using the same file.
      setValue(CatalogueFormField.AnimalFile, '');
    }
  };

  /**
   * Resets the animal table when the file changes
   */
  useEffect(() => {
    reset();
  }, [animalFile, reset]);

  /**
   * Reset the `animals` form value when the catalogueAnimals changes.
   */
  useEffect(() => {
    const value = catalogueAnimals.length ? catalogueAnimals : [];
    setValue(CatalogueFormField.Animals, value);
  }, [catalogueAnimals, setValue]);

  /**
   * Sets the catalogue animals from the API response.
   */
  useEffect(() => {
    setCatalogueRetrieved(true);

    if (!catalogueRetrieved && catalogue.catalogueItems && catalogue.catalogueItems.length) {
      const getAnimalsInfo = async () => {
        const animalsIds = getAnimalIdsFromCatalogue(catalogue.catalogueItems);
        const animalsInfo = await animalApi.getAnimalInfo(animalsIds, catalogue.dbType);
        catalogue.catalogueItems.forEach((animal) => {
          animal.sex = getAnimalSex(animalsInfo, animal.animalId);
        });
        const animals = getAnimals(catalogue.catalogueItems);
        setCatalogueAnimals(animals);
        append(animals);
      };
      getAnimalsInfo();
    }
  }, [append, catalogue, catalogueRetrieved]);

  /**
   * Manually register a field for the animal data to allow required validation
   * to trigger on submit.
   */
  useEffect(() => {
    register(CatalogueFormField.Animals);

    return () => {
      setValue(CatalogueFormField.Animals, '');
      unregister(CatalogueFormField.Animals);
    };
  }, [register, setValue, unregister]);

  /**
   * Stores the file input to allow programmatic trigger of opening.
   */
  useEffect(() => {
    if (control.fieldsRef?.current) {
      setFileInputRef(control.fieldsRef.current[CatalogueFormField.AnimalFile]?.ref as HTMLInputElement);
    }
  }, [control.fieldsRef]);

  return (
    <Grid
      className="sg-add-animals form-container"
      item
      xs={12}
      sm={12}
      md={10}
    >
      <div className="intro">
        <Grid
          container
          justify="center"
        >
          <p>
            Now you will need to load the completed template from Step 1.<br />
            <i>If you missed downloading the file, please click the SAVE button and go back to step-1 to access the template.</i>
          </p>
        </Grid>
      </div>
      <Card
        className="upload"
        elevation={0}
      >
        <span className="upload__label">Upload XLSX&nbsp;template</span>
        <div className="upload__filename">
          {/** Display the filename if no errors */}
          {catalogueAnimals && animalFile
            && !errors[CatalogueFormField.AnimalFile]
            && !errors[CatalogueFormField.Animals] && (
              <>
                <DocCsvOutlinedIcon />
                <span>{animalFile?.name}</span>
              </>
          )}
          {/** Display any form errors (file type, size) */}
          {errors[CatalogueFormField.AnimalFile] && (
            <div className="upload__error">
              <AlertIcon />{errors[CatalogueFormField.AnimalFile].message}
            </div>
          )}
          {/** No file uploaded */}
          {errors[CatalogueFormField.Animals] && errors[CatalogueFormField.Animals].message && (
            <div className="upload__error">
              <AlertIcon />{errors[CatalogueFormField.Animals].message}
            </div>
          )}
        </div>
        <label
          className="upload__button"
          htmlFor="browse-files"
        >
          <input
            className="hidden"
            name={CatalogueFormField.AnimalFile}
            id="browse-files"
            accept=".xlsx"
            type="file"
            ref={register}
            disabled={loading}
            onClick={inputClick}
            onChange={handleFileChange}
          />
          <Button
            component="div"
            variant="text"
            loading={loading}
          >
            Browse files
          </Button>
        </label>
      </Card>
      {/** Animal line item error messages, only display if animals are returned */}
      {lineItemErrors.map((error, index) => (
        <p
          className="upload__error text--error"
          key={`${error.errorMessage}-${index}`}
        >
          <AlertIcon />{error.errorMessage}
        </p>
      ))}
      {/** Animal table */}
      {!!catalogueAnimals.length && (
        <>
          {/** Animals table */}
          <div className="animals">
            <h4 className="animals__number">{getAnimalCount(catalogueAnimals)}</h4>
            <Divider />
            <Table
              className="sg-table__table"
            >
              <TableHead className="sg-table__header">
                <TableRow>
                  <TableCell className="cell">
                    {isSale && (
                      <>Lot</>
                    )}
                    {isSemen && (
                      <>#</>
                    )}
                  </TableCell>
                  <TableCell className="cell">Animal ID</TableCell>
                  <TableCell className="cell">Sex</TableCell>
                  <TableCell className="cell">
                    <div className="animal-input">
                      {/** Doses (Semen catalogue only) */}
                      {isSemen && (
                        <div className="animal-input__header animal-input__doses">Doses</div>
                      )}
                      <div className="animal-input__header animal-input__price">Price</div>
                    </div>
                  </TableCell>
                  <TableCell className="cell" />
                </TableRow>
              </TableHead>
              <TableBody>
                {fields.map((animal, index: number) => (
                  <TableFade
                    index={index}
                    key={animal.id}
                  >
                    <Card
                      className="sg-table__row"
                      component={TableRow}
                    >
                      <TableCell className="td td__lot td--padding-top">
                        {/** Sale - lot */}
                        {isSale && (
                          !!animal.lot && animal.lot
                        )}
                        {/** Semen - # */}
                        {isSemen && (
                          index + 1
                        )}
                      </TableCell>
                      <TableCell className="td td__animal-id td--padding-top">
                        {animal.displayId}
                      </TableCell>
                      <TableCell className="td td__sex">
                        <TextField
                          defaultValue={getSexDesc(animal.sex)}
                          disabled
                        />
                      </TableCell>
                      <TableCell className="td td__price">
                        <div className="animal-input">
                          {/** Doses (Semen catalogue only) */}
                          {isSemen && (
                            <TextField
                              className="animal-input__doses"
                              defaultValue={animal.doses}
                              name={`animals[${index}].doses`}
                              error={!!errors.animals?.[index]?.doses}
                              helperText={(
                                <FormErrorMessage
                                  errors={errors}
                                  name={`animals[${index}].doses`}
                                />
                              )}
                              inputRef={register(catalogueFormValidation[CatalogueFormField.Doses])}
                            />
                          )}
                          <TextField
                            className="animal-input__price"
                            defaultValue={animal.price}
                            name={`animals[${index}].price`}
                            InputProps={{
                              ...defaultTextFieldProps.InputProps,
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                            error={!!errors.animals?.[index]?.price}
                            helperText={(
                              <FormErrorMessage
                                errors={errors}
                                name={`animals[${index}].price`}
                              />
                            )}
                            inputRef={register(catalogueFormValidation[CatalogueFormField.AnimalPrice])}
                          />
                        </div>
                      </TableCell>
                      <TableCell className="td__delete">
                        <Tooltip
                          title="Delete animal"
                          PopperProps={{
                            className: 'sg-animal-tags__tooltip',
                            placement: 'top',
                          }}
                        >
                          <IconButton
                            onClick={() => deleteAnimal(index)}
                          >
                            <DeleteOutlinedIcon color="primary" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </Card>
                  </TableFade>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
      <ConfirmationDialog
        confirmLabel="Yes, delete"
        cancelLabel="No, cancel"
        title="Upload a new file"
        onCancel={() => setConfirmationOpen(false)}
        onConfirm={() => confirmReplaceFile()}
        open={confirmationOpen}
      >
        Uploading a new file will delete the current file permanently and any changes you’ve made.
        <br /><br />
        Do you want to proceed?
      </ConfirmationDialog>
    </Grid>
  );
};
